import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import useData from "../../hooks/useData";

import Page from "./Page";
import Card from "../layout/Card";

const PROJECT_ENTITY_OPTIONS = [
  { key: "calls", label: "Calls" },
  { key: "surveyAssignment", label: "Survey Assignment" },
  { key: "locations", label: "Locations" },
];

const ProjectRefresh = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executeAction, getFromServer } = useData();

  const [projectId, setProjectId] = useState(location.state?.projectId || null);
  const [clientId, setClientId] = useState(location.state?.clientId || null);
  const [project, setProject] = useState(null);

  const [callDateFrom, setCallDateFrom] = useState("");
  const [callDateTo, setCallDateTo] = useState("");
  const [scheduledUsers, setScheduledUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);

  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");

  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setIsLoadingProject(false);
      return;
    }

    const loadProject = async () => {
      setIsLoadingProject(true);
      const results = await getFromServer({
        entityName: "Project",
        id: projectId,
      });
      setProject(results || null);
      setIsLoadingProject(false);
    };

    loadProject();
  }, [projectId]);

  useEffect(() => {
    setPreview(null);
  }, [callDateFrom, callDateTo, selectedUsers, selectedEntities]);

  useEffect(() => {
    const canLoad = !!projectId && !!callDateFrom && !!callDateTo;
    if (!canLoad) {
      setScheduledUsers([]);
      setSelectedUsers([]);
      return;
    }

    const loadUsers = async () => {
      setIsLoadingUsers(true);
      const results = await executeAction({
        entityName: "SyncRefreshAudit",
        action: "getScheduledUsers",
        data: {
          projectId,
          callDateFrom,
          callDateTo,
        },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setScheduledUsers(results.data);
      } else {
        setScheduledUsers([]);
      }
      setSelectedUsers([]);
      setIsLoadingUsers(false);
    };

    loadUsers();
  }, [projectId, callDateFrom, callDateTo]);

  const toggleEntity = (key) => {
    setSelectedEntities((current) => {
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  };

  const handlePreview = async () => {
    if (!projectId) return;

    setIsPreviewing(true);

    const selectedUserIds = selectedUsers
      .map((user) => user.id)
      .filter((id) => !!id);

    const results = await executeAction({
      entityName: "SyncRefreshAudit",
      action: "previewProjectRefresh",
      data: {
        projectId,
        callDateFrom,
        callDateTo,
        selectedUserIds,
        selectedEntities,
      },
      displayMessage: true,
    });

    if (results?.isSuccessful) {
      setPreview(results.data || null);
    } else {
      setPreview(null);
    }

    setIsPreviewing(false);
  };

  const handleApply = async () => {
    if (!preview?.audit?.id) return;
    if (!reason.trim()) return;

    setIsApplying(true);

    const results = await executeAction({
      entityName: "SyncRefreshAudit",
      action: "applyRefresh",
      data: {
        auditId: preview.audit.id,
        reason: reason.trim(),
      },
      displayMessage: true,
    });

    if (results?.isSuccessful) {
      setPreview((current) => {
        if (!current) return current;
        return {
          ...current,
          audit: results.data?.audit || current.audit,
        };
      });
    }

    setIsApplying(false);
  };

  const canPreview =
    !!projectId &&
    !!callDateFrom &&
    !!callDateTo &&
    selectedEntities.length > 0 &&
    !isPreviewing;
  const canApply = !!preview?.audit?.id && !!reason.trim() && !isApplying;

  return (
    <Page
      pageName="Project Refresh"
      entityName={project?.name || "Project"}
      includeToolbar
      items={[{ name: "back", iconName: "ArrowCircleLeft" }]}
      onToolItemClick={(name) => {
        if (name === "back") {
          navigate("/project", { state: { projectId, clientId } });
        }
      }}
    >
      <Box
        display="flex"
        gap={2}
        flexDirection="column"
      >
        <Card title="Scope">
          {isLoadingProject ? (
            <Box
              p={2}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <CircularProgress size={16} />
              <Typography variant="body2">Loading project...</Typography>
            </Box>
          ) : (
            <Box p={2}>
              <Typography
                variant="body2"
                sx={{ mb: 2 }}
              >
                Project: {project?.name || "Unknown"}
              </Typography>

              <Box
                display="flex"
                gap={2}
                alignItems="flex-start"
                flexWrap="wrap"
              >
                <TextField
                  label="Call Date From"
                  type="date"
                  value={callDateFrom}
                  onChange={(e) => setCallDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  required
                  sx={{ minWidth: 180 }}
                />

                <TextField
                  label="Call Date To"
                  type="date"
                  value={callDateTo}
                  onChange={(e) => setCallDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  required
                  sx={{ minWidth: 180 }}
                />

                <Autocomplete
                  multiple
                  options={scheduledUsers}
                  value={selectedUsers}
                  loading={isLoadingUsers}
                  onChange={(event, value) => setSelectedUsers(value || [])}
                  getOptionLabel={(option) =>
                    `${option.name || "Unknown"}${option.callCount ? ` (${option.callCount})` : ""}`
                  }
                  sx={{ minWidth: 340, flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Scheduled Users (Optional)"
                      size="small"
                    />
                  )}
                />
              </Box>
            </Box>
          )}
        </Card>

        <Card title="Entities to Refresh">
          <Stack
            spacing={0}
            p={2}
          >
            {PROJECT_ENTITY_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    checked={selectedEntities.includes(option.key)}
                    onChange={() => toggleEntity(option.key)}
                  />
                }
                label={option.label}
              />
            ))}
          </Stack>
        </Card>

        <Card title="Preview">
          <Box
            p={2}
            display="flex"
            gap={2}
            flexDirection="column"
          >
            <Box>
              <Button
                variant="contained"
                disabled={!canPreview}
                onClick={handlePreview}
              >
                {isPreviewing ? "Previewing..." : "Preview Counts"}
              </Button>
            </Box>

            {preview?.warningShown ? (
              <Alert severity="warning">{preview.warningMessage}</Alert>
            ) : null}

            {preview?.counts ? (
              <Box>
                {Object.entries(preview.counts).map(([key, value]) => (
                  <Typography
                    key={key}
                    variant="body2"
                  >
                    {key}: {value}
                  </Typography>
                ))}
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 1 }}
                >
                  Total Records: {preview.previewTotalCount || 0}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Card>

        <Card title="Apply Refresh">
          <Box
            p={2}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              required
              multiline
              minRows={2}
            />

            <Box>
              <Button
                variant="contained"
                disabled={!canApply}
                onClick={handleApply}
              >
                {isApplying ? "Queueing..." : "Apply Refresh"}
              </Button>
            </Box>

            {preview?.audit?.status ? (
              <Typography variant="body2">
                Status: {preview.audit.status}
              </Typography>
            ) : null}
          </Box>
        </Card>
      </Box>
    </Page>
  );
};

export default ProjectRefresh;
